import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {api} from '../../api';


function MultiInput({ values, onChange, placeholder, type = 'text' }) {
  const addValue = () => onChange([...values, '']);
  const updateValue = (i, v) => onChange(values.map((val, idx) => idx === i ? v : val));
  const removeValue = (i) => onChange(values.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-1">
      {values.map((v, i) => (
        <div key={i} className="flex gap-1">
          <input
            type={type}
            placeholder={placeholder}
            value={v}
            onChange={(e) => updateValue(i, e.target.value)}
            className="flex-1 px-3 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-600"
          />
          <button type="button" onClick={() => removeValue(i)} className="text-gray-400 hover:text-blue-600 px-1">&times;</button>
        </div>
      ))}
      <button type="button" onClick={addValue} className="text-xs text-blue-600 hover:text-blue-800">+ Add {placeholder.toLowerCase()}</button>
    </div>
  );
}

export default function CompanyPicker({ selected = [], onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef();

  const search = useCallback((q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    api(`/companies?q=${encodeURIComponent(q)}`)
      .then(r => r.json())
      .then(data => setResults(data.filter(c => !selected.some(s => s.companyId === c.id))))
      .catch(() => {});
  }, [selected]);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 250);
    return () => clearTimeout(timer);
  }, [query, search]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const add = (company) => {
    onChange([...selected, { companyId: company.id, companyName: company.name, title: '', emails: [], phones: [] }]);
    setQuery('');
    setResults([]);
  };

  const remove = (companyId) => onChange(selected.filter(c => c.companyId !== companyId));

  const update = (companyId, field, value) => {
    onChange(selected.map(c => c.companyId === companyId ? { ...c, [field]: value } : c));
  };

  return (
    <div ref={ref} className="relative">
      <div className="space-y-2 mb-2">
        {selected.map(c => (
          <div key={c.companyId} className="p-3 bg-blue-50 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Link to={`/crm/companies/${c.companyId}`} className="text-sm font-medium text-blue-700 hover:underline">{c.companyName}</Link>
              <button type="button" onClick={() => remove(c.companyId)} className="text-blue-400 hover:text-blue-600">&times;</button>
            </div>
            <input
              type="text"
              placeholder="Title"
              value={c.title}
              onChange={(e) => update(c.companyId, 'title', e.target.value)}
              className="w-full px-3 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-600"
            />
            <MultiInput values={c.emails || []} onChange={(v) => update(c.companyId, 'emails', v)} placeholder="Email" type="email" />
            <MultiInput values={c.phones || []} onChange={(v) => update(c.companyId, 'phones', v)} placeholder="Phone" />
          </div>
        ))}
      </div>
      <input
        type="text"
        placeholder="Search companies by name..."
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
      />
      {open && results.length > 0 && (
        <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {results.map(c => (
            <li key={c.id}>
              <button type="button" onClick={() => { add(c); setOpen(false); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">
                {c.name}
                {c.industry && <span className="text-gray-400"> — {c.industry}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && query.length >= 2 && results.length === 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg px-4 py-3 text-sm text-gray-500">
          No companies found
        </div>
      )}
    </div>
  );
}
